import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ClassificationReview.css';
import {
  apiMessage,
  BLIND_BUCKET,
  getCounts,
  getDetail,
  getStats,
  listCategories,
  listQueue,
  saveMark,
  SIGNAL_FIELDS,
  SIGNAL_VERDICTS,
} from '../services/serviceInboxApi';
import EmailBody from './EmailBody';
import { catColor, catLabel } from './categories';

/**
 * Classification review — docs/CLASSIFICATION_REVIEW_UI.md.
 *
 * Deliberately shaped like service_inbox's standalone classification report
 * (report.py): the same tiles, filter chips, sortable table, and click-to-expand
 * detail row, so anyone who has read that report can use this without relearning
 * it. The review form is the one thing the report has no equivalent for, and it
 * lives inside the expanded row.
 *
 * A new component rather than another section of GoldLayer.js, which is ~5,000
 * lines and which the design says not to extend.
 *
 * Two things this page captures that a CSV cannot: whether the reviewer was shown
 * the model's answer (server-assigned, snapshotted per mark), and whether each
 * extracted customer signal is right.
 *
 * **This page owns no data.** The queue, the bodies, the marks and the category
 * vocabulary are all served by the service inbox, which holds the emails. What
 * is local is the interaction: the open row, the draft in progress, and the
 * text search over whatever page is on screen.
 *
 * The category chips are deliberately *not* in that local list, and used to be.
 * Counted off `page.items` they described one page of 100 while reading as a
 * description of the queue — "Billing 1" against forty billing emails — and
 * muting one hid rows on this page while leaving its siblings on every other.
 * Both halves now come from the server (`/meta/counts` and `?category=`), so the
 * chips, the table and the pager are three views of one filter.
 */

const PAGE_SIZE = 100;


const FILTERS = { unreviewed: false, reviewed: true, all: null };

// What the customer lookup concluded, in the words a reviewer thinks in. Five
// states rather than found/not-found, because three of them mean "no customer"
// for three different reasons and only one says anything about the customer:
//
//   no_match             searched every key, nobody is there — a new customer,
//                        or one filed under details this email does not carry
//   insufficient_signals the email carried nothing to search on
//   error                the platform could not be reached; says nothing at all
//
// `tone` drives the colour, and error is deliberately the loud one: a lookup
// that failed must never be read as a customer who does not exist.
const MATCH_STATES = {
  matched: { tone: 'ok', label: 'matched' },
  ambiguous: { tone: 'warn', label: 'more than one' },
  no_match: { tone: 'none', label: 'not found' },
  insufficient_signals: { tone: 'none', label: 'nothing to match on' },
  not_attempted: { tone: 'none', label: 'not looked up' },
  error: { tone: 'bad', label: 'lookup failed' },
};

// Who. The ServiceTitan id is deliberately not beside it: a reviewer identifies
// a customer by name and address, and the id is a routing fact they never type
// anywhere on this page — the same reason `tenant_location` is shown and
// `tenant_id` is not. It stays as the fallback for a record with no name at all,
// which is the one case where nothing else would identify the row.
const customerName = (c) => c.name || `#${c.customer_id}`;

/*
 * Candidate rows collapsed to one entry per customer.
 *
 * The platform returns a row per path it reached a customer by, so one customer
 * arrives two or four times over — which is exactly why `customer_count` counts
 * distinct `(tenant_id, customer_id)` pairs rather than rows. Rendering the rows
 * drew four identical cards under a heading that said "2 possible customers",
 * and made two React children share a key.
 *
 * Grouped on the same tuple the count uses, so the cards and the number above
 * them can no longer disagree. Distinct addresses are kept rather than dropped:
 * one customer with two service addresses is one answer with two places.
 */
/* Same order as the signals panel above, so the reviewer reads one list twice
 * rather than two lists once. */
const AGREE_FIELDS = ['name', 'phone', 'email', 'zip', 'address'];

/* Strongest agreement wins when a customer's rows disagree.
 *
 * One customer is reachable through several rows and each carries its *own*
 * address, so the per-signal verdict is per row, not per customer. Keeping the
 * first row's verdict would report "differs" for a customer whose second
 * service address matches the email exactly, purely on row order. */
const AGREE_RANK = { exact: 4, close: 3, differs: 2, unknown: 1 };
const bestAgreement = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  const out = {};
  for (const k of AGREE_FIELDS) {
    out[k] = (AGREE_RANK[a[k]] || 0) >= (AGREE_RANK[b[k]] || 0) ? a[k] : b[k];
  }
  return out;
};

/* A customer reached both ways is reached both ways, and saying only one of
 * them would hide the half that explains a wrong match. */
const mergeVia = (a, b) => (!a || !b || a === b ? a || b : 'both');

const groupCandidates = (rows) => {
  const by = new Map();
  for (const r of rows || []) {
    const k = `${r.tenant_id}-${r.customer_id}`;
    const seen = by.get(k);
    if (!seen) {
      by.set(k, { ...r, addresses: r.address ? [r.address] : [] });
    } else {
      if (r.address && !seen.addresses.some((a) => sameAddress(a, r.address))) {
        seen.addresses.push(r.address);
      }
      seen.agreement = bestAgreement(seen.agreement, r.agreement);
      seen.matched_via = mergeVia(seen.matched_via, r.matched_via);
    }
  }
  return [...by.values()];
};

/*
 * Whether two spellings are the same place, for display only.
 *
 * One customer's rows differ by ZIP+4 and by case — "…FL, 34787" beside
 * "…FL, 34787-8411" — and stacked as two address lines that reads as two
 * addresses. Normalising the four-digit add-on and the casing is safe here in a
 * way it is not in `CustomerMatch.location`, which compares exact strings on
 * purpose: that one decides which address a service visit is sent to, and this
 * one only decides whether to draw a second line under a name.
 */
const sameAddress = (a, b) => {
  const norm = (s) => s.replace(/-\d{4}\b/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  return norm(a) === norm(b);
};

/*
 * The one ambiguous shape this page narrows for itself: a residential customer
 * and a new-construction record, and nothing else.
 *
 * A homeowner whose system DelAir installed under a builder contract is in
 * ServiceTitan twice — once under the RNC tenant from the build, once under the
 * office that services them — so a phone or an email finds two records and the
 * platform calls it ambiguous. It is not: new construction is out of scope for
 * this inbox, which leaves exactly one record the reviewer could act on. Drawn
 * as "2 customers" it costs a call back to learn something the pair already
 * says.
 *
 * Deliberately the narrowest rule that covers it. **Exactly one RNC record and
 * exactly one residential one**, over distinct customers rather than rows, with
 * the true `customer_count` agreeing so a capped list cannot pass as a pair.
 * Two residential records, a commercial one beside an RNC one, three of
 * anything, a type the platform did not send — all stay as they are, because
 * only this shape has an out-of-scope half that makes the other half the
 * answer. Everything else is a genuine crowd and a genuine call back.
 *
 * This is display, not routing: the payload still says `ambiguous`, the stored
 * match still names nobody, and the platform still picks no candidate. The RNC
 * record is not dropped either — the detail pane lists it, carrying its "DelAir
 * RNC" office pill, which is where the reviewer sees what the other half was.
 */
const rncPair = (customer) => {
  if (!customer || customer.status !== 'ambiguous' || customer.customer_count !== 2) return null;
  const shown = groupCandidates(customer.candidates);
  const rnc = shown.filter((x) => x.is_rnc);
  const rest = shown.filter((x) => !x.is_rnc);
  if (shown.length !== 2 || rnc.length !== 1 || rest.length !== 1) return null;
  if ((rest[0].customer_type || '').trim().toLowerCase() !== 'residential') return null;
  return {
    // One address or none, on the queue's rule for a resolved match: two
    // spellings the grouping could not reconcile are a real gap, and a cell is
    // no place to guess which of them a truck would go to. The detail pane still
    // draws both, from `addresses`.
    primary: { ...rest[0], address: rest[0].addresses.length === 1 ? rest[0].addresses[0] : '' },
    other: rnc[0],
  };
};

/*
 * The queue's customer column, standing where confidence used to.
 *
 * A column rather than another line in the email cell, because "who is this
 * from?" is a question the eye runs down the list to answer, the same way it
 * does for the verdict mark.
 *
 * **It only ever says something when there is a customer to say it about.** The
 * three no-customer states — nobody found, nothing to search on, not a category
 * we look up — are the common case down a page of a hundred rows, and spelled
 * out they fill the column with a hundred restatements of "no". Empty says the
 * same thing and leaves the found ones as the only marks in the column, which is
 * what makes them findable at a glance. The detail pane still gives all five
 * states in words: which kind of nothing this was is a real question, it is just
 * not one the queue is scanned for.
 *
 * `error` is the exception and stays drawn, because it is not a no: the platform
 * could not be reached and this row says nothing whatever about the customer.
 * Blanked, an outage would read as a page of new customers.
 *
 * Whether the cell is drawn at all is `showCustomer`'s call, not this one's — a
 * blind row nobody has ruled on shows nothing here.
 */
const CustomerCell = ({ customer }) => {
  if (!customer) return null;
  // A residential record beside a new-construction one is one customer written
  // down twice, and the column says so: the name, not "2 customers". The RNC
  // half is not lost, it is in the pane the row opens.
  const pair = rncPair(customer);
  const state = pair
    ? MATCH_STATES.matched
    : MATCH_STATES[customer.status] || MATCH_STATES.not_attempted;
  const c = pair ? pair.primary : customer.customer;
  // Resolved to one, or narrowed to a handful — both are a customer found.
  if (!c && customer.status !== 'ambiguous' && customer.status !== 'error') return null;
  return (
    <span className={`cust ${state.tone}`} title={customer.error || undefined}>
      <span className="dot" />
      <span className="who">
        {c ? (
          // Three lines, most identifying first: who they are, where the work
          // would go, whose office owns them. Stacked rather than run together
          // on one line because an address is long enough to push the column
          // wide, and because the eye going down the queue is looking for the
          // name — which stays flush at the top of every cell that has one.
          <>
            <span className="cname">{customerName(c)}</span>
            {/* Both are omitted rather than dashed when absent. An address is
                withheld whenever the candidate rows spell it two ways, which is
                a real gap and not worth a placeholder in a queue cell. */}
            {c.address && <span className="csub">{c.address}</span>}
            {/* The office as a pill, not a third line of grey text. It is the
                one part of the cell that repeats across rows — a handful of
                offices over a whole queue — so it reads as a tag on the
                customer rather than more of their address, which is what it
                looked like sitting directly under one. */}
            {c.tenant_location && <span className="tenant">{c.tenant_location}</span>}
          </>
        ) : customer.status === 'ambiguous' ? (
          `${customer.customer_count} customers`
        ) : (
          state.label
        )}
      </span>
    </span>
  );
};

/*
 * The detail pane's customer block.
 *
 * Sits directly above the signal ticker because it is what the signals
 * produced: the reviewer reads who was matched, then rules on whether the name
 * and phone that found them belonged to the customer at all.
 *
 * Drawn on blind rows, like the signals are. The blind slice withholds the
 * *category* so the label stays independent; this is derived from the signals
 * and says nothing about what the model thought the email wanted.
 */
/*
 * One customer, in the three lines the queue cell uses and in the same order,
 * so the row you clicked and the pane it opened read as one thing.
 *
 * Shared by the resolved single match and by every candidate of an ambiguous
 * one. A candidate is not a lesser kind of match — it is a customer nobody has
 * ruled out yet — and running them together on one line each was the thing that
 * made an ambiguous row hard to read: name, address and office collapsed into a
 * sentence, so telling two near-identical records apart meant parsing text
 * rather than comparing the same field in the same place twice over.
 */
/* How a record lines up with the email, per signal.
 *
 * Three states and a fourth that draws nothing:
 *   exact   — the record carries the same value
 *   close   — the same value written differently. A middle initial the record
 *             omits, "Dr" against "Drive", a street the email trailed a city
 *             onto. This is the state that explains a miss: "Fred W. Edwards"
 *             against a record reading "Fred Edwards" is exactly why that
 *             lookup stayed ambiguous instead of resolving.
 *   differs — compared, and not the same
 *   unknown — email and phone only. The record carries neither value and the
 *             platform runs one key, so when the key was the email nobody ever
 *             looked at anyone's phone. Distinct from "differs" on purpose: an
 *             unchecked signal drawn as a mismatch is a reason to doubt a
 *             record that nothing is actually against.
 *   null    — the email carried no such signal, so nothing was compared. Drawn
 *             as nothing at all, never as a cross.
 *
 * All five are shown. When the key was the email, every candidate carries that
 * address by construction and it ticks for all of them — which is the point on
 * the shape this was built for, where the wrong customer agreed on the email
 * and on nothing else. Showing only the three the record can answer read as if
 * the other two had been forgotten.
 */
const AGREE_MARK = { exact: '✓', close: '≈', differs: '✗', unknown: '?' };
const VIA_LABEL = {
  location: 'a service location',
  customer: 'the customer record',
  both: 'both the customer record and a service location',
};

const SignalAgreement = ({ agreement }) => {
  if (!agreement) return null;
  const shown = AGREE_FIELDS.filter((k) => agreement[k]);
  if (!shown.length) return null;
  return (
    <div className="sigagree">
      {shown.map((k) => (
        <span key={k} className={`sigchip sig-${agreement[k]}`}>
          {AGREE_MARK[agreement[k]]} {k}
        </span>
      ))}
    </div>
  );
};

const CustomerFacts = ({ c, on }) => (
  <>
    <div className="custname">
      {customerName(c)}
      {/* The ServiceTitan customer id, shown only here and never in the queue
          column. It is what you take to ServiceTitan to look the record up, and
          it is the one field that tells two candidates apart when the name, the
          address and the office all read the same. Suppressed when the name is
          already the id, which is what `customerName` falls back to. */}
      {c.customer_id != null && c.name && <span className="custid">#{c.customer_id}</span>}
    </div>
    {/* One line per distinct address the customer's rows carried — usually one.
        A customer with two service addresses is one answer with two places, not
        two answers. */}
    {(c.addresses?.length ? c.addresses : [c.address || '—']).map((a) => (
      <div className="faddr" key={a}>
        {a}
      </div>
    ))}
    {/* The pill earns most on a candidate: two records with one name and one
        address are told apart by nothing but the office. */}
    {c.tenant_location && (
      <div>
        <span className="tenant">{c.tenant_location}</span>
      </div>
    )}
    {/* Which of the extracted signals this record actually agrees with.
        The key that found the crowd is the same for every card in it, so on its
        own it separates nothing: one email address on two unrelated customer
        records drew "matched on email" twice, while one of the two agreed on
        name, ZIP and street and the other agreed on none of them.

        A signal the email never carried is absent rather than crossed. There is
        a difference between "checked, and the record disagrees" and "there was
        nothing to check", and a row of crosses for signals nobody extracted
        reads as a record that contradicts the email. */}
    <SignalAgreement agreement={c.agreement} />
    {/* The same closing line a single resolved match ends on, so a card in a
        stack of six is not a shorter kind of block than a card on its own.
        "matched" rather than "resolved": these are the records the key landed
        on, and the platform deliberately resolves none of them.

        `matched_via` rides on the end of it because it qualifies the same fact:
        an email key reaching a customer through one of their service locations
        is a different thing from the address sitting on the customer record
        itself, and when a lookup returns the wrong person it is usually the
        second. */}
    {on && (
      <div className="faddr custwhy">
        matched on {on}
        {c.matched_via && ` · via ${VIA_LABEL[c.matched_via] || c.matched_via}`}
      </div>
    )}
  </>
);

const CustomerBlock = ({ customer }) => {
  if (!customer) return null;
  const pair = rncPair(customer);
  const state = pair
    ? MATCH_STATES.matched
    : MATCH_STATES[customer.status] || MATCH_STATES.not_attempted;
  const c = pair ? pair.primary : customer.customer;
  return (
    <>
      <div className="rlbl">Customer</div>
      <div className={`custblock ${state.tone}`}>
        {/* A collapsed pair draws **both records as cards**, the kept one marked.
            The two are near-identical — same person, same street, differing by
            customer id and office — so the first arrangement, which promoted one
            to the block's own heading and put the other under "Also found",
            asked the reviewer to compare a heading against a card. Two cards in
            the same shape, one of them highlighted, is the same information as a
            comparison: the eye checks the one field that differs and moves on.

            The other record keeps its "DelAir RNC" pill, which is still the only
            thing on the page that says new construction. */}
        {pair ? (
          <div className="custlist">
            {/* No "matched on …" line on either card, unlike the candidate list:
                one key found both of these, and the closing line below says so
                once. On the two cards it would be the third and fourth time the
                pane says "email" about the same lookup. */}
            <div className="faddr custalso">Selected</div>
            <div className="custcard chosen">
              <CustomerFacts c={pair.primary} />
            </div>
            <div className="faddr custalso">Also found</div>
            <div className="custcard">
              <CustomerFacts c={pair.other} />
            </div>
          </div>
        ) : c ? (
          <CustomerFacts c={c} />
        ) : (
          <div className="custname">
            {customer.status === 'ambiguous'
              ? `${customer.customer_count} possible customers`
              : state.label}
          </div>
        )}

        {/* Listed, never narrowed to one. Choosing among candidates is Phase B's
            job and the platform declines to do it too — a confident wrong match
            writes against a stranger's record, an ambiguous one costs a call
            back. Showing who they are is how the reviewer judges the signals. */}
        {!pair &&
          customer.candidates?.length > 0 &&
          (() => {
            const shown = groupCandidates(customer.candidates);
            return (
              <div className="custlist">
                {shown.map((x) => (
                  <div className="custcard" key={`${x.tenant_id}-${x.customer_id}`}>
                    <CustomerFacts c={x} on={customer.matched_on} />
                  </div>
                ))}
                {/* Normally silent: the review API caps nothing, so the stack is
                    every customer the lookup reached and this line does not
                    draw. It survives for the one truncation left — the
                    platform's own 25-candidate bound, which a shared mailbox or
                    a main office line can still hit. Said out loud rather than
                    left as a silent disagreement between a number and a stack,
                    which is the bug this block has been through twice. */}
                {customer.customer_count > shown.length && (
                  <div className="faddr custmore">
                    showing {shown.length} of {customer.customer_count}
                  </div>
                )}
              </div>
            );
          })()}

        <div className="faddr custwhy">
          {/* Said plainly, because the pane is disagreeing with the lookup: the
              platform called this ambiguous and the page is showing one answer.
              A reviewer who cannot see why would be right not to trust it. */}
          {pair &&
            `${customer.matched_on} reached this customer twice — the other record is new construction, which is not this inbox's work`}
          {!pair && customer.status === 'matched' && `resolved on ${customer.matched_on}`}
          {!pair &&
            customer.status === 'ambiguous' &&
            `${customer.matched_on} matched more than one record — this one needs a call back`}
          {customer.status === 'no_match' &&
            'searched every key and found nobody — a new customer, or one filed under details this email does not carry'}
          {customer.status === 'insufficient_signals' &&
            'the email carried nothing to look a customer up by'}
          {customer.status === 'not_attempted' &&
            'not a category a customer lookup applies to'}
          {/* Says nothing about the customer, and must not read as "no such
              customer". The row is worth re-running once the platform is back. */}
          {customer.status === 'error' &&
            `the lookup could not be run: ${customer.error || 'unknown error'}`}
        </div>
      </div>
    </>
  );
};

/*
 * The verdict, drawn on the thing it is a verdict about.
 *
 * It had a column of its own at the far right of the row, which put the answer
 * an inch of table away from the question: the category was decided in one cell
 * and ruled on in another, with the customer sitting between them. On the
 * category the mark reads as one statement — *Billing, and a human agreed* —
 * and the table gives a whole column back to the cells that needed the width.
 *
 * Glyph and colour, never colour alone: ✓ and ✗ are still different marks in
 * greyscale and to a red/green colourblind reviewer, where a green dot and a red
 * dot are one mark drawn twice.
 *
 * `agreed` is computed server-side because the two halves are not the same
 * field — a prefilled mark says so in `category_correct`, while a blind mark's
 * agreement is its independent label matching the row's CURRENT category, which
 * the payload withholds on a withheld row — so it cannot be derived here. That
 * the blind half reads the live category and the prefilled half reads the
 * snapshot is the service's rule and the reason `staleAgainst` draws nothing on
 * a blind row. It is one bit about a blind row's prediction,
 * disclosed deliberately; see `MarkSummary.agreed` in the service for the
 * argument. `null` stays neutral: signals ticked but no category ruled on, or a
 * blind mark left unlabelled. All three are "reviewed", which is the first thing
 * the mark says and the reason it is drawn at all.
 */
const VerdictMark = ({ verdict, stale }) => {
  if (!verdict) return null;
  const tone =
    verdict.agreed === true ? ' agreed' : verdict.agreed === false ? ' disagreed' : '';
  return (
    <span
      className={`vmark${tone}`}
      // Who ruled and when, in the tooltip rather than the row: the mark answers
      // "is this done", the name answers "who do I ask about it".
      //
      // `stale` names the category the model gave at the time, because on a
      // drifted row "agreed with the model" is true of an answer the model no
      // longer gives, and the tooltip is the one place there is room to say
      // which one.
      title={
        `marked by ${verdict.reviewer} ${new Date(verdict.reviewed_at).toLocaleString()}` +
        (verdict.agreed === true
          ? ' — agreed with the model'
          : verdict.agreed === false
            ? ' — disagreed with the model'
            : '') +
        (stale ? `, which then answered ${catLabel(stale)}` : '')
      }
    >
      {verdict.agreed === false ? '✗' : '✓'}
    </span>
  );
};

// Two different facts, and the UI needs both. `row.blind` says the row is in the
// blind slice, which stays true whether or not you asked to see its prediction
// and is what makes a verdict on it count as prefilled rather than independent.
// `withheld` says this particular response carried no prediction, which is the
// only thing that decides what can be drawn.
const withheld = (row) => row.blind && !row.revealed;

// The category the model gave at the moment this row's verdict was cast, when
// that is no longer the category the row carries. Null when nothing drifted.
//
// `predicted_category_at_review` is a snapshot precisely so a re-classification
// cannot rewrite history under a recorded verdict. The queue was drawing the
// verdict against the row's *current* category anyway, which made the snapshot
// invisible and the tick a claim about an answer nobody had ruled on. Measured
// 2026-08-31: 41 of production's 89 marked rows have drifted, 33 of them with
// the prediction on screen. The shape that prompted this is the worst of the
// 33 — eleven rows now reading `Lead` carry a green tick cast when the model
// said `Appointment request`, and `Lead` was not a category any reviewer could
// pick at the time they cast it. "Lead, and a human agreed"
// was a sentence the page had no basis for, and it is the reason a re-mark
// request went out by email in the first place: the rows needing one could not
// be seen from here.
//
// Null on ANY blind row, revealed included, and that is the rule rather than an
// oversight. Drift is a statement about what the reviewer ruled on, and a blind
// reviewer ruled on nothing: the snapshot was withheld from them by
// construction, so "reviewed as Appointment request" names a comparison they
// never made. Their label is a judgement of the *email*, and it does not go
// stale when the model changes its mind — only a confirmation does, which is
// why the prefilled half still draws this.
//
// Withheld rows were already out, the payload stripping the snapshot there.
// Revealing one put the chip back, which is the case this was reopened for: a
// Yelp relay labelled `Lead` blind, before `lead` was a category any reviewer
// could pick, drew "Lead — blind — reviewed as General ✗ → Lead". Everything
// after the first word was wrong about it.
//
// It also has to be null for the tick beside it to mean anything. The service
// scores a blind mark's `agreed` against the row's CURRENT category rather than
// the snapshot (`0cacda4`, the same argument made server-side), so a chip
// reading "reviewed as X" next to a tick computed against Y would hang the mark
// on the one category it is not about.
const staleAgainst = (row) => {
  if (row.blind) return null;
  const snapshot = row.verdict?.predicted_category_at_review;
  if (!snapshot || snapshot === row.prediction?.category) return null;
  return snapshot;
};

// Whether the customer lookup's answer may be drawn for this row.
//
// A blind row that nobody has ruled on yet is the one place it is held back.
// The reviewer's whole job there is to name the category from the email alone,
// and a resolved customer is a strong hint about what the email is: a name and
// a service address say "existing customer with a system we installed" before
// the message has been read, which is most of the way to an appointment or a
// warranty. Withholding the model's category and then handing over the lookup
// the same signals produced would buy back the anchoring the blind slice exists
// to remove.
//
// Once a verdict is recorded the label is fixed and cannot be influenced, so the
// row shows the customer like any other — the reviewer revisiting their own call
// should see everything known about it. Revealing the prediction lifts it too:
// the anchor is already on screen and there is nothing left to protect.
//
// Client-side, unlike the prediction, which the server strips. The two are
// protecting different things. A prediction in the payload would make the
// accuracy figures worthless whether or not it was drawn, so it must not be
// sent; this is about what the reviewer's eye lands on, and not drawing it is
// the whole of that.
const showCustomer = (row) => !withheld(row) || !!row.verdict;

// Bucketing a row for the chips used to happen here, and that was the bug: the
// chips described the loaded page. The server buckets the whole filter now, by
// the same rule — predicted category, or `blind` where the prediction is
// withheld and there is nothing to bucket it by.

const pct = (v) => (v == null ? '—' : `${v}%`);

// Received splits over two lines, date above time. One long `toLocaleString()`
// was the widest thing in the row and pushed the subject over; stacked, the
// column is as narrow as a date and both halves stay scannable. Seconds are
// dropped — nothing here turns on them.
const fmtDate = (s) => new Date(s).toLocaleDateString();
const fmtTime = (s) => new Date(s).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

// How far below the scrollport's top edge an opened row is parked. The sticky
// table header occupies the first 40px (`.verdict` clears it by the same
// figure), and the rest is breathing room so the row reads as sitting below the
// header rather than jammed against it. The row must stay fully visible: it is
// what you click again to close, and leaving it under the header is the bug this
// offset fixes.
const OPEN_ROW_OFFSET = 52;

// The nearest ancestor that actually scrolls, or null for the window.
//
// Not a detail worth guessing at: `Layout` wraps every page in
// `<main className="flex-1 overflow-auto">` inside an `h-screen` flex shell, so
// the window does not scroll at all here and `window.scrollTo` is a no-op. That
// `main` is also the scrollport the table's sticky header sticks to. Resolved by
// walking the DOM rather than by selector so this keeps working if the shell
// changes, and falls back to the window for a page that is not inside it.
const scrollParentOf = (el) => {
  for (let n = el.parentElement; n; n = n.parentElement) {
    const oy = window.getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n;
  }
  return null;
};

const ClassificationReview = () => {
  const [stateFilter, setStateFilter] = useState('unreviewed');
  const [page, setPage] = useState({ items: [], total: 0, offset: 0 });
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState('');
  // Per-bucket sizes for the whole filter, from the server. `null` until the
  // first answer, which is what keeps the strip from flashing an empty row.
  const [counts, setCounts] = useState(null);
  // The chip that is filtering, or null for all. One at a time: a chip carrying
  // a count reads as "show me these", and the server takes one category, so the
  // page cannot promise a combination it would then have to assemble locally —
  // which is the class of bug this whole change removes.
  const [category, setCategory] = useState(null);
  // Newest first, matching report.py's `dir: -1` on received and the order the
  // server already returns rows in. The queue is a working stack: the email that
  // arrived an hour ago is the one worth ruling on, not one from six weeks back.
  const [sort, setSort] = useState({ key: 'received_at', dir: 'desc' });
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Marks saved since this page was loaded. On the unreviewed filter the marked
  // rows leave the list, so counting `verdict` there would always read zero and
  // the reviewer would lose any sense of progress within the sitting.
  const [markedHere, setMarkedHere] = useState(0);
  // Ask the server for blind rows' predictions as well. A display mode, not a
  // filter: the same rows come back either way. It has to be a request rather
  // than a render decision because a blind row's prediction is not in the
  // payload at all — the server strips it, so there is nothing here to un-hide.
  const [reveal, setReveal] = useState(false);
  // The re-mark backlog: rows a reviewer ruled on where the model has since
  // changed its answer and theirs is not the new one either. A toggle rather
  // than a category chip because it cuts across every category — picking it
  // must not clear whatever else is selected, or it would silently answer a
  // different question from the one asked.
  //
  // Server-side, like `category`: the client holds one page, so filtering here
  // could only hide rows already fetched and would disagree with the pager.
  const [needsRemark, setNeedsRemark] = useState(false);
  // The other backlog, and the mirror of the one above: blind rows whose
  // independent label is not the model's current answer — the rows the blind
  // accuracy figure counts wrong. 93 on production 2026-09-01, of which a hand
  // adjudication read 53 as reviewer error; the number had been visible on the
  // stats tile for weeks with no way to list the rows behind it.
  //
  // Disjoint from `needsRemark` by construction — a mark's reviewer either saw
  // the prediction or did not — so the two toggles turn each other off. Both on
  // is an empty page, which is a control that can only mislead.
  const [blindDisagreed, setBlindDisagreed] = useState(false);
  // The thread being read, or null for the whole queue. `{id, count, from}` —
  // `count` so the banner can say how big the thread is without waiting for the
  // page, and `from` to put the reviewed filter back on the way out.
  //
  // A filter, not a view mode: the server returns the thread's messages as
  // ordinary rows and nothing is grouped, collapsed or reordered. Collapsing a
  // thread into one row has no honest answer for `total`, for the category
  // chips or for blindness, every one of which is per message.
  const [thread, setThread] = useState(null);
  const threadId = thread?.id ?? null;

  // Where the pager is. Held in a ref rather than read off `page.offset` so that
  // reloading cannot depend on the state the reload replaces. Changing the
  // filter resets it — a different set has a different length — while toggling
  // reveal leaves it alone and redraws the page you were on.
  const offsetRef = useRef(0);

  const loadQueue = useCallback(
    async (offset = 0) => {
      setLoading(true);
      offsetRef.current = offset;
      try {
        setPage(
          await listQueue({
            reviewed: FILTERS[stateFilter],
            limit: PAGE_SIZE,
            offset,
            reveal,
            category,
            conversationId: threadId,
            needsRemark,
            blindDisagreed,
          })
        );
        setOpenId(null);
        setMarkedHere(0);
        setError(null);
      } catch (err) {
        setError(apiMessage(err, 'Could not load the review queue'));
      } finally {
        setLoading(false);
      }
    },
    [stateFilter, reveal, category, threadId, needsRemark, blindDisagreed]
  );

  const loadStats = useCallback(async () => {
    try {
      setStats(await getStats());
    } catch {
      // Non-fatal: the tiles go blank, reviewing still works.
    }
  }, []);

  // Deliberately not keyed on `category`: picking a chip must not renumber the
  // chips. They describe how the unfiltered set divides, and that is the thing a
  // reviewer navigates by — a strip that collapsed to the one bucket they had
  // just chosen would leave nothing to choose next.
  const loadCounts = useCallback(async () => {
    try {
      setCounts(await getCounts({ reviewed: FILTERS[stateFilter], reveal }));
    } catch {
      // Non-fatal, like the tiles: the chips disappear, the queue still works.
      setCounts(null);
    }
  }, [stateFilter, reveal]);

  useEffect(() => { loadQueue(offsetRef.current); }, [loadQueue]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  useEffect(() => {
    // The dropdown's options. On failure the field falls back to free text and
    // the server still rejects an unknown category, so this is degraded rather
    // than broken.
    listCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const item = page.items.find((i) => i.id === openId) || null;

  // Fetch the open row's body, signals and marks, and seed the draft from the
  // caller's own current verdict. Keyed on openId alone: re-seeding whenever the
  // page changes would wipe edits in progress.
  const wanted = useRef(null);
  useEffect(() => {
    setDetail(null);
    if (openId === null) return setDraft(null);

    // Seeded from the row's verdict whoever cast it, so opening a row someone
    // else ruled on is a revision of their answer rather than a blank form:
    // saving appends under the current reviewer and the newest mark wins.
    //
    // With the whole category answer held back on a drifted row. Both
    // `category_correct` and `corrected_category` are answers to "what did the
    // model get wrong here", and on a row the model has since re-answered that
    // question no longer exists — so neither can be carried across. The signal
    // ticks and the note are about the *email* and survive.
    //
    // Seeding only one of the two was live for a few hours on 2026-08-31 and
    // produced contradictory marks: the reviewer met a pre-filled correction
    // from a *colleague*, answering a question about a category the row no
    // longer had, clicked "Right" on the new one, and saved both. Scoring
    // survives that — `_prefilled_agreement` lets the tick win — but
    // `label_for_mark` reads `corrected_category` FIRST, so the labels export
    // and `evaluate --from-db` took the stale correction as ground truth for a
    // row the reviewer had just confirmed. Rows 529 and 1714 were corrected by
    // hand; this is what stops it recurring.
    //
    // A blind row never drifts (see `staleAgainst`), so its own label seeds back
    // like any other: it was a judgement of the email and it still stands. Blind
    // rows are outside the `Needs re-mark` set server-side as well, so nothing
    // in that flow moves with this.
    const row = page.items.find((i) => i.id === openId);
    const current = row?.verdict;
    const drifted = !!(row && staleAgainst(row));
    setDraft({
      category_correct: drifted ? null : (current?.category_correct ?? null),
      corrected_category: drifted ? '' : (current?.corrected_category ?? ''),
      signal_marks: current?.signal_marks ?? {},
      notes: current?.notes ?? '',
    });

    wanted.current = openId;
    // The same `reveal` the queue was fetched with, so the row and its detail
    // cannot disagree about whether the prediction is on screen. Toggling closes
    // whatever is open (the reload clears `openId`), so this cannot go stale.
    getDetail(openId, { reveal })
      .then((d) => { if (wanted.current === openId) setDetail(d); })
      .catch((err) => {
        // A reviewer clicks through rows faster than the network answers; only
        // the row still open may report a failure.
        if (wanted.current === openId) setError(apiMessage(err, 'Could not load this email'));
      });
  }, [openId]); // eslint-disable-line

  // Bring the opened row to the top of the window.
  //
  // The detail panel is inserted *below* the row you clicked, so opening the
  // last row on a page puts the whole thing past the fold: the click looks like
  // it did nothing, and the fix — scroll down and find it — is the reviewer's
  // to discover. Hundreds of rows a sitting makes that expensive.
  //
  // Keyed on `draft` as well as `openId` because the panel renders on
  // `open && draft`, and `draft` is set in the effect above: on the commit where
  // `openId` changes the panel is not in the DOM yet, so scrolling there lands
  // short by the panel's own height. `scrolledFor` holds this to once per opened
  // row — `draft` also changes on every keystroke in the form, and re-scrolling
  // mid-edit would yank the page out from under whoever is typing.
  const openRowRef = useRef(null);
  const scrolledFor = useRef(null);
  // Where the queue was sitting before the row was opened, so closing it puts
  // the reviewer back rather than leaving them parked wherever the open row had
  // travelled to. Recorded immediately before each auto-scroll and consumed on
  // close, so the return trip is always the exact inverse of the trip out.
  const restoreTo = useRef(null);
  useEffect(() => {
    if (openId === null) {
      scrolledFor.current = null;
      const back = restoreTo.current;
      restoreTo.current = null;
      // Runs on the commit that already removed the panel, so the position is
      // measured against the collapsed page; a target past the new bottom is
      // clamped by the browser rather than needing a guard here.
      if (back) {
        if (back.el) back.el.scrollTo({ top: back.top, behavior: 'smooth' });
        else window.scrollTo({ top: back.top, behavior: 'smooth' });
      }
      return;
    }
    if (scrolledFor.current === openId) return;
    // The panel renders on `open && draft`; until it is there the row has
    // nothing under it to scroll into view.
    if (!draft) return;
    const row = openRowRef.current;
    if (!row) return;
    scrolledFor.current = openId;
    // Computed rather than `scrollIntoView`, which lands the row flush at the
    // scrollport's top and therefore under the sticky header, leaving only its
    // bottom edge showing — you had to scroll *up* to reach the row you had just
    // clicked in order to close it. `scroll-margin-top` is the documented fix for
    // that and does not reliably apply to a `<tr>`, which is not a normal block
    // box, so the offset is subtracted here instead.
    const sc = scrollParentOf(row);
    restoreTo.current = { el: sc, top: sc ? sc.scrollTop : window.scrollY };
    if (sc) {
      const top =
        row.getBoundingClientRect().top -
        sc.getBoundingClientRect().top +
        sc.scrollTop -
        OPEN_ROW_OFFSET;
      sc.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    } else {
      const top = row.getBoundingClientRect().top + window.scrollY - OPEN_ROW_OFFSET;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }, [openId, draft]);

  // Biggest bucket first, so the chips are ordered by where the work is. Ties
  // break by name rather than by object key order, which is insertion-ordered
  // and would let two equal buckets swap places between refetches.
  const buckets = useMemo(() => {
    const c = { ...(counts || {}) };
    // A selected bucket stays on screen at zero. Switching to "Already
    // reviewed" can empty the category you were working, and a chip that
    // vanishes takes with it the only control that clears the filter it is
    // still applying — leaving an empty table with no visible cause.
    if (category && !(category in c) && counts) c[category] = 0;
    return Object.entries(c).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [counts, category]);

  const visible = useMemo(() => {
    // Text search stays local, and stays honest about it: unlike the chips it
    // has no server-side counterpart, so it can only search the loaded page.
    // The placeholder says so.
    const q = query.trim().toLowerCase();
    const rows = page.items.filter((i) => {
      if (!q) return true;
      return (
        (i.subject || '').toLowerCase().includes(q) ||
        (i.sender || '').toLowerCase().includes(q) ||
        (i.sender_name || '').toLowerCase().includes(q)
      );
    });
    // Sorting is page-local for the same reason, and unlike the search it is
    // not a filter — the rows are all here, only their order changes.
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const get = (r) =>
        sort.key === 'category'
          ? r.prediction?.category || ''
          : sort.key === 'subject'
          ? (r.subject || '').toLowerCase()
          : r.received_at || '';
      const x = get(a);
      const y = get(b);
      return x < y ? -dir : x > y ? dir : 0;
    });
  }, [page.items, query, sort]);

  // On the unreviewed filter a marked row is gone from `items`, so the count of
  // this sitting's saves is the only thing left to report.
  const reviewedCount =
    FILTERS[stateFilter] === false ? markedHere : page.items.filter((i) => i.verdict).length;

  // The buckets partition the filter, so their sum is the queue — and it is the
  // queue *before* a chip narrows it, which `page.total` no longer is. Falls
  // back to `page.total` when the counts request failed, where the two agree
  // because no chip can be selected without them.
  const queueTotal = counts
    ? Object.values(counts).reduce((a, b) => a + b, 0)
    : page.total;

  // Withheld, not "in the blind slice" — under `reveal` the slice is unchanged
  // but nothing is being withheld, so the segment drops out rather than
  // reporting a number that no longer describes what is on screen.
  const withheldCount = counts?.[BLIND_BUCKET] ?? 0;

  // Click to filter, click again to clear. Selecting a bucket restarts the
  // pager: a different set has a different length, exactly as changing the
  // reviewed half does.
  const pickCategory = (k) => {
    offsetRef.current = 0;
    setCategory((current) => (current === k ? null : k));
  };

  // Read one thread. The count on the badge is over the whole reviewable
  // corpus, so the two other filters are cleared on the way in or the page
  // could show two of the three it just promised: `all` rather than the
  // reviewed half a reviewer is usually in, and no category, since a thread is
  // not a bucket and intersecting the two would make the chips describe a set
  // nobody asked for. The previous half is remembered and restored on the way
  // out — leaving a reviewer in `all` after a detour is a filter they did not
  // choose and cannot see they are in.
  const openThread = (i) => {
    if (!i.conversation_id || i.thread_count < 2) return;
    offsetRef.current = 0;
    setCategory(null);
    setStateFilter('all');
    setThread({ id: i.conversation_id, count: i.thread_count, from: stateFilter });
  };

  const closeThread = () => {
    offsetRef.current = 0;
    if (thread?.from) setStateFilter(thread.from);
    setThread(null);
  };

  // Clicking the active column flips it; picking a new one starts at the
  // sensible end for that column, as report.py does — dates newest first,
  // everything else A→Z.
  const sortBy = (key) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'received_at' ? 'desc' : 'asc' }
    );

  const setSignal = (field, verdict) =>
    setDraft((d) => {
      const marks = { ...(d.signal_marks || {}) };
      // Unticking removes the key: "not judged" is a real answer, and distinct
      // from "na", which says the email carried no such signal.
      if (marks[field] === verdict) delete marks[field];
      else marks[field] = verdict;
      return { ...d, signal_marks: marks };
    });

  // A withheld row's whole task is naming a category, and the server rejects a
  // mark without one. Catching it here costs a round trip and says so in
  // place, rather than surfacing a 422 after the reviewer has moved on. A
  // revealed row is exempt because it has a prediction to rule on, and the
  // server's own check is on the same footing.
  const blindNeedsCategory = !!item && withheld(item) && !draft?.corrected_category;

  const save = useCallback(async () => {
    if (!item || !draft || saving || blindNeedsCategory) return;
    setSaving(true);
    try {
      const mark = await saveMark(item.id, {
        category_correct: draft.category_correct,
        corrected_category: draft.corrected_category || null,
        signal_marks: draft.signal_marks,
        notes: draft.notes || null,
        // What this reviewer could actually see, taken from the row the server
        // sent rather than from the `reveal` toggle: the row is the record of
        // what was disclosed when it was drawn. The server records
        // `shown_prediction` from it, so understating it here would file a
        // verdict read off the screen as an independent label.
        revealed: !!item.revealed,
      });
      // Reconcile the page locally rather than refetching, which would renumber
      // everything mid-pass. On the unreviewed filter the row has just left the
      // server's queue, so drop it — leaving it on screen means the list only
      // agrees with the server after a reload. On the other filters the row still
      // belongs here, so it stays and carries its new mark.
      setPage((p) =>
        FILTERS[stateFilter] === false
          ? {
              ...p,
              items: p.items.filter((i) => i.id !== item.id),
              total: Math.max(0, p.total - 1),
            }
          : { ...p, items: p.items.map((i) => (i.id === item.id ? { ...i, verdict: mark } : i)) }
      );
      setMarkedHere((n) => n + 1);
      setError(null);
      loadStats();
      // The row has just moved between the reviewed and unreviewed halves, so
      // the bucket it sits in has changed size. The page above is reconciled by
      // hand for the same reason it is not refetched — renumbering mid-pass —
      // but the chips are not in the reviewer's way, so they can simply be right.
      loadCounts();
      // Close the row rather than opening the next one: which email to judge
      // next is the reviewer's call, and auto-advancing lands them in a form
      // for a message they have not read yet.
      setOpenId(null);
    } catch (err) {
      setError(apiMessage(err, 'Could not save mark'));
    } finally {
      setSaving(false);
    }
  }, [item, draft, saving, blindNeedsCategory, loadStats, loadCounts, stateFilter]);

  const th = (key, label) => (
    <th data-k={key} className={sort.key === key ? sort.dir : ''} onClick={() => sortBy(key)}>
      {label}
    </th>
  );

  return (
    <div className="creport">
      <h1>Classification Review</h1>
      {/* Every figure here is over the whole queue, not the loaded page. When a
          chip is filtering, the narrowed count leads and the queue total stays
          beside it — otherwise selecting Billing would silently redefine "in
          this queue" and there would be nothing on screen saying how much had
          been set aside. */}
      <div className="meta">
        {category && (
          <>
            <strong>{page.total}</strong>{' '}
            {category === BLIND_BUCKET ? 'blind' : catLabel(category)} · of{' '}
          </>
        )}
        {/* "marked", not "marked by you": on the reviewed and all filters this
            counts rows carrying a verdict whoever cast it, because the team
            rules on an item once between them. Only the unreviewed filter's
            figure is this reviewer's own, and there it is this sitting's saves
            rather than a claim about the corpus. */}
        {queueTotal} in this queue · {reviewedCount} marked
        {!reveal && withheldCount > 0 && <> · {withheldCount} blind</>}
      </div>

      {error && <div className="err">{error}</div>}

      <div className="tiles">
        <div className="tile">
          <div className="v">{stats ? stats.emails_reviewed : '—'}</div>
          <div className="k">emails reviewed</div>
        </div>
        <div className="tile good">
          <div className="v">{pct(stats?.blind_accuracy)}</div>
          <div className="k">blind accuracy</div>
        </div>
        <div className="tile warn">
          <div className="v">{pct(stats?.prefilled_agreement)}</div>
          <div className="k">prefilled agreement</div>
        </div>
        {/* `prefill_bias` had a tile here until 2026-08-31 and deliberately no
            longer does. It is still served, and the service still computes it
            correctly — differencing the two figures that ask the same question,
            "did the human land on what the model said at the time".

            It is not a health metric. Its one job is the SOW's labeled set:
            `--prefill-predictions` buys a packet cheap enough to come back at
            the cost of a number biased toward agreement, and a returned file
            cannot be told apart by its shape, so a prefilled score has to be
            discounted by something. That is what the figure is for, and it is
            the answer to a question nobody on this page is asking.

            Drawn beside two live percentages it read as one, and it was wrong
            in a different way each time anyone looked: 27.1 while it was
            measuring a taxonomy migration, then 0.3 once the blind half moved
            to scoring against the model's current answer and the subtraction
            stopped comparing like with like. A reader had no way to tell. With
            real blind labels in hand there is nothing here to estimate. */}
      </div>

      {/* Per-field extraction accuracy, from the signal ticks in the form. A
          strip rather than four more tiles: these six numbers are about a
          different component from the four above — the extractor pulling a
          phone number out, not the classifier choosing a category — and giving
          them equal weight would read as one row of eight unrelated figures.

          Hidden until something has been judged. Six "—" chips would take up
          the space of a finding while reporting nothing. */}
      {stats?.signal_fields?.some((f) => f.scored > 0) && (
        <div className="sigscores">
          <span className="lbl">signal accuracy</span>
          {/* The server scores every field it knows, including `location_hint`,
              which this form does not ask about — it is a routing hint rather
              than customer identity. Showing a field no reviewer can tick would
              leave one chip permanently blank. A field the form *does* offer
              stays visible while unjudged, because "nobody has looked" is worth
              seeing; so does an unexpected field, if one ever gets scored. */}
          {stats.signal_fields
            .filter((f) => f.scored > 0 || SIGNAL_FIELDS.includes(f.field))
            .map((f) => (
              <span
                key={f.field}
                className={`sigscore${f.scored ? '' : ' none'}`}
                // The counts behind the percentage. 100% off two ticks and 100%
                // off two hundred are the same number and not the same claim.
                title={
                  f.scored
                    ? `${f.correct} correct · ${f.incorrect} incorrect · ` +
                      `${f.unsure} unsure · ${f.na} n/a (not counted)`
                    : 'nobody has judged this field yet'
                }
              >
                {f.field}
                <span className="v">{pct(f.accuracy)}</span>
                {f.scored > 0 && <span className="n">{f.scored}</span>}
              </span>
            ))}
        </div>
      )}

      {/* Counts and membership both come from the server. Selecting a chip is a
          refetch, not a re-render of what is already here — the page holds 100
          rows and the bucket may run to hundreds, so there is nothing local to
          filter. A selected chip is the only one pressed; clicking it again
          clears the filter.

          Blind is a bucket like any other and can be selected, which is the one
          way to work the withheld slice deliberately. Under `reveal` the server
          stops emitting it, because nothing is being withheld to count. */}
      {/* While a thread is open the banner stands in for the chips. They count
          the whole filter and the page is one conversation, so a chip pressed
          here would intersect two filters and leave the strip describing a set
          that is not on screen — the pill-disagrees-with-the-page failure the
          counts endpoint exists to avoid. One way out, always visible. */}
      {thread ? (
        <div className="threadbar">
          {/* The way out leads, in the house's primary-action style, because it
              is the only control in a mode the reviewer entered with one click
              and has no other way to leave. It sat at the end of the strip in
              the chips' own quiet style and read as the least important thing
              on a line whose other two items are just prose. */}
          <button type="button" className="tbback" onClick={closeThread}>
            <span aria-hidden="true">←</span> Back to the queue
          </button>
          {/* Plain words, because a reviewer is not a mail administrator:
              "conversation" is what their mail client calls this, and "reading
              one thread — 3 messages" was a status line written for whoever
              built it. */}
          <span className="tb">
            Showing all {thread.count} email{thread.count === 1 ? '' : 's'} in this
            conversation
          </span>
          {/* The reviewed half is off while this is open, and saying so beats a
              reviewer wondering why rows they have already ruled on are back. */}
          <span className="tbnote">including ones already reviewed</span>
        </div>
      ) : (
      <div className="legend">
        {buckets.map(([k, n]) => (
          <button
            key={k}
            type="button"
            className={`chip${category === k ? ' on' : ''}`}
            aria-pressed={category === k}
            title={
              category === k
                ? 'Showing only these — click to clear'
                : `Show only ${k === BLIND_BUCKET ? 'blind' : catLabel(k)}`
            }
            onClick={() => pickCategory(k)}
          >
            <span
              className="dot"
              style={{ background: k === BLIND_BUCKET ? 'var(--serious)' : catColor(k) }}
            />
            {k === BLIND_BUCKET ? 'Blind' : catLabel(k)}
            <span className="n">{n}</span>
          </button>
        ))}
      </div>
      )}

      <div className="controls">
        <select
          value={stateFilter}
          onChange={(e) => {
            // A different filter is a different set with a different length, so
            // the pager starts over. Reveal deliberately does not do this.
            offsetRef.current = 0;
            // The mirror of the re-mark toggle switching this. "To review" and
            // the re-mark backlog are disjoint by construction, so leaving both
            // on would strand the reviewer on an empty page with a checked box
            // and nothing on screen saying why. Same move `reveal` makes when
            // it dissolves the blind bucket out from under a selection.
            if (e.target.value === 'unreviewed') setNeedsRemark(false);
            setStateFilter(e.target.value);
          }}
        >
          <option value="unreviewed">To review</option>
          <option value="reviewed">Already reviewed</option>
          <option value="all">All</option>
        </select>
        {/* "on this page" is not a hedge, it is the scope. Unlike the chips
            this has no server-side counterpart, so it searches the loaded rows
            and nothing behind them. Saying so is what stops it being read as a
            search of the queue — which is exactly how the chips misled. */}
        <input
          type="search"
          placeholder="Find on this page by subject or sender…"
          value={query}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
        />
        {/* The page's one generic escape hatch, so it has to clear everything
            that narrows the page — the thread included. The banner carries its
            own way out and says where it goes, but this is the control a
            reviewer reaches for reflexively, and finding it greyed out while
            the queue is plainly filtered reads as the page being stuck. */}
        <button
          type="button"
          disabled={!query && !category && !thread}
          onClick={() => {
            setQuery('');
            offsetRef.current = 0;
            setCategory(null);
            closeThread();
          }}
        >
          Clear
        </button>
        {/* A checkbox rather than another chip: the chips filter which rows are
            drawn, and this changes what the server sends about all of them. It
            refetches, because a blind row's prediction was never in the payload
            to begin with. */}
        <label className="reveal" title="Blind rows normally arrive without their prediction">
          <input
            type="checkbox"
            checked={reveal}
            onChange={(e) => {
              const on = e.target.checked;
              // Revealing dissolves the blind bucket into the real categories,
              // so a selection of it has nothing left to show — and no chip left
              // to clear it with, since the server stops emitting the bucket.
              // Drop the selection rather than strand the reviewer on an empty
              // page. Any other selection survives, but what is in it changes as
              // the blind rows join, so the pager restarts.
              if (category) offsetRef.current = 0;
              if (on && category === BLIND_BUCKET) setCategory(null);
              setReveal(on);
            }}
          />
          Show blind predictions
        </label>
        {/* The re-mark backlog. A checkbox beside `reveal` rather than a chip in
            the strip above: chips are a partition of the queue and exactly one
            is selected at a time, while this cuts across all of them and has to
            combine with whichever is chosen. Selecting it must not silently
            clear the category.

            Hidden entirely when the backlog is empty. Nothing needs re-marking
            is the good state, and a permanent control reading "0" invites
            clicking it to find out what it does, which is an empty page. It
            reappears on its own the next time a re-classification moves a row
            out from under a verdict.

            The count comes from the queue response rather than /meta/counts:
            that endpoint is a partition summing to the queue total, and a
            cross-cutting number there would be added into it twice. */}
        {(needsRemark || page.needs_remark_total > 0) && (
          <label
            className="reveal remark"
            title={
              'Rows a reviewer already ruled on where the model has since ' +
              'changed its answer, and the reviewer\u2019s answer is not the ' +
              'new one either \u2014 so the verdict on record settles nothing. ' +
              'Blind rows are never included.'
            }
          >
            <input
              type="checkbox"
              checked={needsRemark}
              onChange={(e) => {
                const on = e.target.checked;
                // A different set, so the pager restarts.
                offsetRef.current = 0;
                // Every row in this set carries a mark, so it is a strict
                // subset of "Already reviewed" and is empty under "To review" —
                // the default. Switching the dropdown is therefore not the
                // filter quietly overriding a choice, it is the only reading
                // under which the toggle returns anything at all. The category
                // filter *is* left alone, because that one genuinely combines.
                if (on && stateFilter === 'unreviewed') setStateFilter('reviewed');
                // Disjoint sets — see the blind-disagreement toggle below.
                if (on) setBlindDisagreed(false);
                setNeedsRemark(on);
              }}
            />
            Needs re-mark
            <span className="remarkn">{page.needs_remark_total}</span>
          </label>
        )}
        {/* The blind half of the same idea, and it appears on the same terms:
            only when there is something in it, or when it is already on.

            Its rows are the ones `blind_accuracy` counts wrong, so this is the
            control that turns that percentage into work. It discloses nothing
            the row does not already carry — every row in the set is drawn with
            a ✗, which is `MarkSummary.agreed`, served on blind rows
            deliberately since 2026-08-13.

            Worth knowing before working it: on a *withheld* row the reviewer's
            own label is not drawn on the row (`fixcat` is gated on `withheld`),
            so judging from the queue alone is not possible — open the row, or
            turn `Show blind predictions` on and compare side by side. A re-mark
            made under reveal is recorded as prefilled, which is what a
            correction is. */}
        {(blindDisagreed || page.blind_disagreed_total > 0) && (
          <label
            className="reveal remark blinddis"
            title={
              'Blind rows where the label a reviewer gave is not the ' +
              'category the model now assigns \u2014 the rows the blind ' +
              'accuracy figure counts wrong. Some are reviewer errors and ' +
              'some are the model\u2019s; re-marking settles which. Prefilled ' +
              'rows are never included.'
            }
          >
            <input
              type="checkbox"
              checked={blindDisagreed}
              onChange={(e) => {
                const on = e.target.checked;
                offsetRef.current = 0;
                // Same reasoning as the toggle above: every row here carries a
                // mark, so the set is empty under "To review" and switching the
                // dropdown is the only reading under which the toggle returns
                // anything. The category filter genuinely combines and is left
                // alone.
                if (on && stateFilter === 'unreviewed') setStateFilter('reviewed');
                // Disjoint sets, so both on is always nothing.
                if (on) setNeedsRemark(false);
                setBlindDisagreed(on);
              }}
            />
            Blind disagreements
            <span className="remarkn">{page.blind_disagreed_total}</span>
          </label>
        )}
        {/* No labels.csv link. The export is a developer's artefact — a test set
            for `evaluate` — and the people using this page are verifying
            classifications, not scoring the classifier. It was one control on a
            reviewer's toolbar that no reviewer had a use for. The endpoint is
            unchanged and still reachable; `evaluate --from-db` reads the same
            marks without a file at all. */}
        {/* Only while loading. The row count it used to carry is what the pager
            already says, and said better — "1–100 of 234" locates you as well as
            counts. What the pager cannot say is that a request is in flight. */}
        {loading && <span className="count">Loading…</span>}
      </div>

      <table>
        <thead>
          <tr>
            {th('received_at', 'Received')}
            {th('subject', 'Email')}
            {th('category', 'Category')}
            {/* Not sortable, unlike its neighbours: the useful orderings here
                are by status, and status is not what the column shows on a
                resolved row — it shows a name. Sorting by name is not a
                question anyone asks of a review queue. */}
            <th>Customer</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((i) => {
            const open = i.id === openId;
            const p = i.prediction;
            const stale = staleAgainst(i);
            return (
              <React.Fragment key={i.id}>
                <tr
                  ref={open ? openRowRef : null}
                  className={`main${open ? ' open' : ''}`}
                  onClick={() => setOpenId(open ? null : i.id)}
                >
                  <td className="when">
                    {/* The email cell's two elements, minus its `text-base`: the
                        same shape and colours, one step down in size. Received is
                        a stamp you scan past rather than a heading, and at the
                        subject's size it read as the more important of the two. */}
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {i.received_at ? fmtDate(i.received_at) : '—'}
                      </h4>
                      {i.received_at && (
                        <p className="text-sm text-gray-600">{fmtTime(i.received_at)}</p>
                      )}
                    </div>
                  </td>
                  <td>
                    {/* Tailwind here rather than this file's own classes: it is
                        the same "title over supporting line" cell Bronze and
                        Silver use for a sync row, and it should be built out of
                        the same elements so the three pages stay one house
                        style.

                        `text-base` is doing real work. Tailwind's preflight
                        resets headings to `font-size: inherit`, so Bronze's h4 is
                        16px only because it inherits from `body`; inside
                        `.creport`, which sets a 14px base, the same markup came
                        out a size smaller. Stated explicitly, both match. */}
                    <div>
                      <h4 className="text-base font-medium text-gray-900">
                        {i.subject || '(no subject)'}
                      </h4>
                      {/* `Name <address>`, the way mail itself writes a
                          sender and the way the classifier is shown it
                          (`prompts.py`, "From: {{from_name}} <{{from_address}}>").
                          The name in the darker grey a reviewer scans for, the
                          bracketed address after it in the supporting one.

                          Both, never one — Graph leaves the name blank on plenty
                          of mail, and the address is the thing the extraction
                          and the customer lookup actually ran on, so a row
                          showing only "Jane Doe" hides what was searched. With
                          no name the address stands alone and *unbracketed*:
                          angle brackets are punctuation between two halves, and
                          `<jane@example.com>` on its own reads as a missing
                          field rather than a plain sender. The dash is for a row
                          carrying neither. */}
                      <p className="text-sm text-gray-600">
                        {i.sender_name ? (
                          <>
                            <span className="text-gray-900">{i.sender_name}</span>
                            {i.sender && <> &lt;{i.sender}&gt;</>}
                          </>
                        ) : (
                          i.sender || '—'
                        )}
                      </p>
                      {/* Only when there is a conversation to open. A badge
                          reading "1 in conversation" on 94% of rows is noise, and
                          the 6% that carry a reply are the whole reason this
                          exists — HANDOFF §13, where three replies nine minutes
                          apart each classified as a fresh customer request.

                          `stopPropagation` because the row itself opens the
                          detail pane: this is the one control inside it that
                          means something else. */}
                      {i.thread_count > 1 && (
                        <button
                          type="button"
                          className="threadchip"
                          title="Show all the emails in this conversation"
                          onClick={(e) => {
                            e.stopPropagation();
                            openThread(i);
                          }}
                        >
                          {i.thread_count} in conversation
                        </button>
                      )}
                      {/* The summary is read, not scanned past: it is the one
                          line that says what the email actually wants, and at
                          `text-sm text-gray-600` it was set like the sender
                          address next to it. `text-base` and a darker grey put
                          it on a level with the subject. The subject still leads
                          the cell by weight and colour rather than by size. */}
                      {p?.summary && (
                        <p className="text-base text-gray-700 mt-1">{p.summary}</p>
                      )}
                      {/* Phones only: the Received and Customer columns are
                          hidden there (ClassificationReview.css), so what they
                          say is folded in here. */}
                      <div className="mobonly">
                        {i.received_at && (
                          <p className="text-sm text-gray-500">
                            {fmtDate(i.received_at)} · {fmtTime(i.received_at)}
                          </p>
                        )}
                        {showCustomer(i) && <CustomerCell customer={i.customer} />}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="cat">
                      {withheld(i) ? (
                        <span className="blindnote">blind</span>
                      ) : (
                        <>
                          <span className="dot" style={{ background: catColor(p?.category) }} />
                          {/* Struck through once a human has called it wrong. The
                              model's answer stays on the row — this is a review
                              queue, and what it got wrong is the point of it —
                              but it should not read as the row's category.

                              Not struck on a drifted row: nobody has called
                              *this* answer anything. The strike moves along to
                              the snapshot, with the tick that earned it. */}
                          <span
                            className={
                              !stale && i.verdict?.agreed === false ? 'wrongcat' : undefined
                            }
                          >
                            {catLabel(p?.category)}
                          </span>
                          {/* Revealed rows keep saying they are blind. The prediction
                              is on screen, so the category renders — but a verdict
                              cast here is recorded as prefilled, and a reviewer
                              should not have to remember which mode they left the
                              toggle in to know that. */}
                          {i.revealed && <span className="blindnote sm">blind</span>}
                        </>
                      )}
                      {/* The answer the verdict was actually cast on, between
                          the current category and the tick — so the row reads
                          "Lead, reviewed as Appointment request ✓" rather than
                          "Lead ✓". `VerdictMark`'s own note argues a verdict
                          belongs on the thing it is a verdict about; on a
                          drifted row that thing is the snapshot, and the tick
                          and the correction after it now sit beside it. */}
                      {stale && (
                        <span className="stale">
                          <span className="stalelbl">reviewed as</span>
                          <span className="dot" style={{ background: catColor(stale) }} />
                          <span
                            className={i.verdict?.agreed === false ? 'wrongcat' : undefined}
                          >
                            {catLabel(stale)}
                          </span>
                        </span>
                      )}
                      {/* A disagreement gets its own line on a revealed blind
                          row, and only there. That row draws the most of any:
                          the category, the "blind" badge qualifying it, the
                          verdict, and the correction — so "Lead blind ✗ →
                          Appointment request" is the longest run of chips the
                          cell ever holds, and the one where the eye has to work
                          out which category the cross belongs to.

                          `i.revealed` rather than `reveal && i.blind`: the
                          server already computes exactly this — in the slice
                          *and* disclosed to this caller — and the two facts are
                          kept apart deliberately (see `withheld` above).

                          An agreement never breaks: a ✓ adds nothing to read and
                          a line of its own would give every settled row two. */}
                      <span
                        className={
                          i.revealed && i.verdict?.agreed === false ? 'vline' : undefined
                        }
                      >
                        <VerdictMark verdict={i.verdict} stale={stale} />
                      {/* What it should have been. Only where the model's own
                          answer is already on screen: on a withheld row the
                          reviewer's label *is* the model's category whenever
                          they agreed, so drawing it there would hand over the
                          prediction the blind slice exists to withhold — the
                          one bit disclosed is `agreed`, not the category. */}
                      {!withheld(i) &&
                        i.verdict?.agreed === false &&
                        i.verdict.corrected_category && (
                          <span className="fixcat">
                            <span className="arrow" aria-hidden="true">
                              →
                            </span>
                            <span
                              className="dot"
                              style={{ background: catColor(i.verdict.corrected_category) }}
                            />
                            {catLabel(i.verdict.corrected_category)}
                          </span>
                        )}
                      </span>
                    </span>
                  </td>
                  <td>{showCustomer(i) && <CustomerCell customer={i.customer} />}</td>
                </tr>

                {open && draft && (
                  <tr className="detail">
                    <td colSpan={4}>
                      {/* Evidence left, verdict right, side by side. Stacked, the
                          email ran to 620px and pushed the form off-screen, so
                          judging a row meant scrolling down to read it and back up
                          to answer — hundreds of times a sitting. Now the form is
                          sticky beside the message: scroll the email as far as you
                          like and Save never leaves the screen. Below 1100px there
                          is no room for two columns and it stacks form-first,
                          which is the same trade the old layout made. */}
                      <div className="dgrid">
                        <div className="evidence">
                          {/* Unlabelled: it is the only thing in the column and
                              it looks like an email, so a heading over it spent a
                              line saying what was already obvious. */}
                          {!detail ? <div className="faddr">Loading…</div> : <EmailBody body={detail.body} />}
                        </div>

                        {/* Rendered before the body arrives on purpose: the draft
                            is seeded from the row, so a reviewer who already knows
                            the answer can rule and save without waiting. */}
                        <div className="verdict">
                          <div className="review">
                            {/* Both states of a blind row say so, because both
                                change what the verdict means. Withheld: the label
                                is independent. Revealed: it is not, and the
                                reviewer is told before they answer rather than
                                left to infer it from a toggle at the top of the
                                page. */}
                            {i.blind && (
                              <div className="blindbar">
                                <span className="blindnote">blind row</span>
                                <span className="faddr">
                                  {i.revealed
                                    ? "you asked to see the model's answer, so this verdict is " +
                                      'recorded as prefilled, not as an independent label'
                                    : "the model's answer is withheld so your label is independent"}
                                </span>
                              </div>
                            )}

                            {/* The same drift the queue row shows, restated
                                where the re-mark is actually cast. A reviewer
                                who scrolled straight into the form would
                                otherwise answer "is the category right?" about
                                a category nobody had told them was new, with
                                the previous verdict already filled in beneath.
                                Named categories on both sides: which answer was
                                confirmed and which one now stands is the whole
                                of what makes the row worth revisiting. */}
                            {stale && (
                              <div className="stalebar">
                                <span className="stalelbl">reviewed as</span>
                                <span className="dot" style={{ background: catColor(stale) }} />
                                {catLabel(stale)}
                                <span className="faddr">
                                  the model now answers {catLabel(p?.category)}, so this
                                  verdict was cast on a different question and the row needs
                                  ruling on again
                                </span>
                              </div>
                            )}

                            {/* Above the question it is being judged on. The
                                reviewer reads the email, then the model's claim
                                about it, then rules — and at the foot of the
                                panel that claim sat below the fold of a sticky
                                column, which made consulting it a scroll. A
                                withheld row carries none, that being the point of
                                it, so the block drops out entirely. */}
                            {!withheld(i) && detail?.prediction?.rationale && (
                              <>
                                <div className="rlbl">Rationale</div>
                                <div className="rationale">
                                  {detail.prediction.rationale}
                                  {detail.prediction.secondary_intent && (
                                    <div className="secondary">
                                      Secondary: {catLabel(detail.prediction.secondary_intent)}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}

                            <div className="rlbl">
                              {withheld(i) ? 'What category is this?' : 'Is the category right?'}
                            </div>
                            {!withheld(i) && (
                              <div className="seg">
                                {[['Right', true], ['Wrong', false]].map(([label, val]) => (
                                  <button
                                    key={label}
                                    type="button"
                                    aria-pressed={draft.category_correct === val}
                                    onClick={() => setDraft({ ...draft, category_correct: val })}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                            )}
                            {categories.length ? (
                              <select
                                value={draft.corrected_category}
                                onChange={(e) =>
                                  setDraft({ ...draft, corrected_category: e.target.value })
                                }
                              >
                                <option value="">
                                  {withheld(i) ? '(choose a category)' : '(unchanged)'}
                                </option>
                                {categories.map((c) => (
                                  <option key={c} value={c}>
                                    {catLabel(c)}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <>
                                <input
                                  type="text"
                                  placeholder={withheld(i) ? 'Category' : 'Correct category'}
                                  value={draft.corrected_category}
                                  onChange={(e) =>
                                    setDraft({ ...draft, corrected_category: e.target.value })
                                  }
                                />
                                <div className="faddr" style={{ marginTop: 4 }}>
                                  The category list could not be loaded, so this is free text. An
                                  unrecognised category is rejected when you save.
                                </div>
                              </>
                            )}

                            {/* Above the signal ticker, because it is what the
                                signals produced: who they resolved to is the
                                context for ruling on whether they were the
                                customer's details in the first place. Held back
                                on an unruled blind row for the reason in
                                `showCustomer` — it would anchor the label the
                                blind slice exists to keep independent. */}
                            {showCustomer(i) && <CustomerBlock customer={detail?.customer} />}

                            <div className="rlbl">Are the extracted signals right?</div>
                            {/* One block per field, value above its verdicts,
                                rather than a three-column table: the values are
                                addresses and emails, which wrapped to three lines
                                in a narrow column and left the buttons scattered
                                at uneven heights. A field nothing was extracted
                                for stays clickable — "incorrect" there is how a
                                reviewer reports a signal the model missed. */}
                            <div className="sigs">
                              {SIGNAL_FIELDS.map((f) => {
                                const value = detail?.signals?.[f];
                                return (
                                  <div className={`sig${value ? '' : ' empty'}`} key={f}>
                                    <div className="sighead">
                                      <span className="sigkey">{f}</span>
                                      <span className="sigval">{value || 'not extracted'}</span>
                                    </div>
                                    <span className="verdicts">
                                      {SIGNAL_VERDICTS.map((v) => (
                                        <button
                                          key={v}
                                          type="button"
                                          aria-pressed={draft.signal_marks?.[f] === v}
                                          onClick={() => setSignal(f, v)}
                                        >
                                          {v}
                                        </button>
                                      ))}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>

                            <div className="rlbl">Notes</div>
                            <textarea
                              rows={2}
                              value={draft.notes}
                              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                            />

                            <div className="saverow">
                              <button
                                type="button"
                                className="save"
                                disabled={saving || blindNeedsCategory}
                                onClick={save}
                              >
                                {saving ? 'Saving…' : 'Save'}
                              </button>
                              {blindNeedsCategory && (
                                <span className="faddr">
                                  pick a category first — a blind row has no prediction to rule on
                                </span>
                              )}
                              {/* Names the reviewer, because the form above is
                                  seeded from their verdict and saving replaces
                                  it. Whose answer you are about to overwrite is
                                  the one thing that makes an override a
                                  decision rather than an accident. */}
                              {i.verdict && (
                                <span className="faddr">
                                  marked by {i.verdict.reviewer}{' '}
                                  {new Date(i.verdict.reviewed_at).toLocaleString()}
                                  {i.verdict.shown_prediction ? '' : ' · blind'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <div className="controls" style={{ marginTop: 12 }}>
        <button
          type="button"
          disabled={page.offset === 0}
          onClick={() => loadQueue(Math.max(0, page.offset - PAGE_SIZE))}
        >
          Prev
        </button>
        <span className="count">
          {page.total === 0 ? 0 : page.offset + 1}–{page.offset + page.items.length} of {page.total}
        </span>
        <button
          type="button"
          disabled={page.offset + PAGE_SIZE >= page.total}
          onClick={() => loadQueue(page.offset + PAGE_SIZE)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ClassificationReview;
